import { translate } from 'infras/i18n';
import { useRouter } from 'next/router';
import {
  CustomerStoryItem,
  CustomerStoryListItem,
} from 'presentations/api/schema/contents';
import { ServicePortalContext } from 'presentations/reducers/ServicePortal';
import {
  apiPath,
  customerStoryListPath,
  customerStoryPath,
  webPath,
} from 'presentations/routes/path';
import { setBreadcrumbs } from 'presentations/views/helpers/breadcrumbs';
import { useContext, useEffect, useMemo } from 'react';
import { setEvent } from 'presentations/views/helpers/analytics';
import { useRequest } from 'presentations/views/hooks/request';
import { Amplitude } from 'presentations/views/helpers/analytics/amplitude';

export interface Props {
  customerStory: CustomerStoryItem;
}

export const useCustomerStoryPage = (props: Props) => {
  const { state: serviceState } = useContext(ServicePortalContext);
  const viewer = serviceState.viewer;
  const router = useRouter();

  const maxCols = useMemo(() => {
    const c = props.customerStory;
    let count = 0;
    if (c.kpi1 || c.kpi1Description) count++;
    if (c.kpi2 || c.kpi2Description) count++;
    if (c.kpi3 || c.kpi3Description) count++;
    return count;
  }, [props.customerStory]);

  useEffect(() => {
    if (!serviceState.isInitialized) {
      return;
    }
    setEvent('read customerStory', {
      slug: props.customerStory.slug,
    });
    Amplitude.customerStoryPageView({ slug: props.customerStory.slug });
  }, [serviceState.isInitialized, props.customerStory.id]);

  const customersRequest = useRequest(
    apiPath('/v1/contents/customers', {
      perPage: 3,
      locale: router.locale,
    }),
  );
  const customers: CustomerStoryListItem[] = customersRequest.data || [];
  const link = customerStoryPath(props.customerStory.slug);

  const pageTitle = useMemo(() => {
    return props.customerStory.title;
    // return [props.customerStory.customerName, props.customerStory.title].join(
    //   ': ',
    // );
  }, [props.customerStory.company, props.customerStory.title]);

  const breadcrumbs = useMemo(() => {
    return [
      {
        title: translate('breadcrumbs.Customer Stories'),
        link: customerStoryListPath(),
      },
      {
        title: props.customerStory.customerName,
        link,
      },
    ];
  }, [props.customerStory.company, link]);
  const routes = setBreadcrumbs(breadcrumbs);

  const shareUrl = webPath(link, router.locale);

  return {
    state: {
      viewer,
      routes,
      shareUrl,
      customers,
      maxCols,
      pageTitle,
    },
    actions: {},
  };
};
